import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Sparkles, ShieldCheck, HeartHandshake, MapPin, Clock, MessageCircle, Star } from "lucide-react";
import { api } from "@/lib/api";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";
import SearchBar from "@/components/common/SearchBar";
import PropertyCard from "@/components/property/PropertyCard";
import CollectionsPeekCarousel from "@/components/property/CollectionsPeekCarousel";
import GuestLoveMarquee from "@/components/common/GuestLoveMarquee";
import SocialValidation from "@/components/common/SocialValidation";
import WhyVyagramTable from "@/components/common/WhyVyagramTable";
import FinalCTAWidget from "@/components/common/FinalCTAWidget";
import StructuredData, { orgSchema, faqSchema } from "@/components/common/StructuredData";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  { q: "How is Vyagram different from a typical Airbnb?", a: "Every property is designed, furnished and operated by us. Direct pricing, on-ground support, housekeeping and a consistent premium feel across stays." },
  { q: "Do you offer direct bookings?", a: "Yes. Booking through us skips OTA markups, unlocks our best rates and gives you direct access to our hospitality team." },
  { q: "Are the properties suitable for gatherings?", a: "Select stays welcome small private gatherings with prior approval — within building quiet hours. We are not a loud-party platform." },
  { q: "How are keys and entry handled?", a: "Most stays are hosted — our team receives you, walks you through the space, and is on call throughout your stay." },
];

export default function Home() {
  const cfg = useSiteConfig();
  const [properties, setProperties] = useState([]);
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [heroImage, setHeroImage] = useState("");
  // Public site config carries the auto-counted 5★ total we already surface
  // on the Guest Love pillars — re-use it as the hero's 4th trust chip so the
  // home page "earns" its check-availability panel with live social proof.
  const [_fiveStar, setFiveStar] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get("/properties", { params: { featured: true } }),
      api.get("/collections"),
      api.get("/locations"),
      api.get("/reviews", { params: { featured: true } }),
      api.get("/site-config"),
    ]).then(([p, c, l, r, sc]) => {
      setProperties(p.data);
      setCollections(c.data);
      setLocations(l.data);
      setReviews(r.data);
      setFiveStar(Number(sc.data?.five_star_reviews || 0));
      // Use Sombre's cover image for the hero — Japandi 3BHK with tree views sets the tone.
      const hero = p.data.find((x) => x.slug === "sombre") || p.data.find((x) => x.slug === "fete") || p.data[0];
      if (hero?.hero_image) setHeroImage(hero.hero_image);
    });
  }, []);

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Vyagram Experiences · Design-Led Stays in South Delhi & Gurugram</title>
        <meta name="description" content="A portfolio of design-led stays in South Delhi and Gurugram — cinematic, calm, premium. Direct booking, on-ground hospitality." />
        <link rel="canonical" href="https://vyagram.com/" />
        {/* Preload the hero image so it shows up on first paint instead of
            waiting for React to finish parsing + mounting the <img>. Shaves
            roughly a second off the perceived load on slow-to-mid connections. */}
        {heroImage && <link rel="preload" as="image" href={heroImage} fetchpriority="high" />}
      </Helmet>

      {/* JSON-LD: Organization + FAQPage so search engines can render rich
          snippets and link the brand entity across our properties. */}
      <StructuredData
        id="home-schema"
        data={[
          orgSchema({
            phone: cfg.whatsapp_number ? `+${cfg.whatsapp_number}` : undefined,
            email: cfg.support_email,
            sameAs: cfg.instagram_handle ? [`https://instagram.com/${cfg.instagram_handle}`] : [],
          }),
          faqSchema(faqs),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden isolate">
        {heroImage && (
          <img
            src={heroImage}
            alt=""
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 via-forest/60 to-noir/70" />

        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:px-10 lg:pt-32 lg:pb-28">
          <p className="vyg-eyebrow !text-gold animate-fade-up">Vyagram Experiences</p>
          <h1 className="mt-4 max-w-4xl font-display text-4xl text-parchment sm:text-5xl lg:text-7xl animate-fade-up">
            Design-led stays for the moments you remember.
          </h1>
          <p className="mt-5 max-w-2xl font-editorial text-lg text-parchment/85 sm:text-xl animate-fade-up" data-testid="hero-supporting-line">
            Design-led stays in South Delhi and Gurugram for couples, work trips, shoots,
            private gatherings, and longer escapes.
          </p>
          <p className="mt-3 max-w-xl font-editorial text-base text-parchment/75 sm:text-lg animate-fade-up">
            A portfolio of experience-led stays — crafted for considered, premium, versatile hospitality.
          </p>
          <div className="mt-10 animate-fade-up">
            <SearchBar variant="hero" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-parchment/85">
            <span className="vyg-pill !bg-white/15 !text-parchment !border-parchment/20"><ShieldCheck size={14}/> Direct-booking rates</span>
            <span className="vyg-pill !bg-white/15 !text-parchment !border-parchment/20"><HeartHandshake size={14}/> On-ground hospitality</span>
            <span className="vyg-pill !bg-white/15 !text-parchment !border-parchment/20"><Sparkles size={14}/> Design-first homes</span>
          </div>
        </div>
      </section>

      {/* Featured collections */}
      <section className="vyg-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="vyg-eyebrow">Find your stay</p>
              <h2 className="mt-3 font-display text-4xl leading-tight lg:text-5xl">
                Curated collections
              </h2>
              <p className="editorial-body mt-3 max-w-2xl">
                Every Vyagram stay is designed around a different kind of moment — a quiet
                weekend, a dinner with friends, a work reset, or a space that simply feels
                different from the everyday.
              </p>
            </div>
            <Link to="/stays" className="vyg-link-gold">See all stays <ArrowRight size={14}/></Link>
          </div>
        </div>

        {/* Tinder-style infinite carousel on every screen — the largest card is centered,
            neighbours peek on both sides. */}
        <div className="mt-6">
          <CollectionsPeekCarousel collections={collections} />
        </div>
      </section>

      {/* Social validation — pillars of trust tucked close to the collections row */}
      <SocialValidation className="!py-6 sm:!py-8 lg:!py-10" />

      {/* Featured properties */}
      <section className="vyg-section bg-white/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="vyg-eyebrow">Editor's picks</p>
              <h2 className="mt-3 font-display text-4xl lg:text-5xl">Stays loved by our guests</h2>
            </div>
            <Link to="/stays" className="vyg-link-gold">View full portfolio <ArrowRight size={14}/></Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 6).map((p) => (<PropertyCard key={p.slug} p={p} />))}
          </div>
        </div>
      </section>

      {/* Why Vyagram */}
      <section className="vyg-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="vyg-eyebrow">Why Vyagram</p>
              <h2 className="mt-3 font-display text-4xl lg:text-5xl">A quieter kind of luxury.</h2>
              <p className="editorial-body mt-5 max-w-xl">
                We are a team of architects and hospitality industry veterans who build and run
                each stay ourselves. That means design that doesn&apos;t feel copy-pasted, and
                hospitality that doesn&apos;t feel corporate.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  { t: "Design-led homes", d: "Editorial interiors, tactile materials, thoughtful lighting.", i: Sparkles },
                  { t: "Direct booking", d: "Best rate, no OTA fees, faster support.", i: ShieldCheck },
                  { t: "On-ground team", d: "Daily housekeeping and live support, through your stay.", i: HeartHandshake },
                  { t: "Real neighborhoods", d: "Stays close to what matters — Dhan Mill, GK 1, Uppal Southend.", i: MapPin },
                ].map((x) => (
                  <div key={x.t} className="rounded-xl border border-forest/10 bg-white p-5">
                    <x.i size={18} className="text-gold" />
                    <p className="font-display text-xl mt-3">{x.t}</p>
                    <p className="text-sm text-noir/70 mt-1">{x.d}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative hidden lg:block">
              {properties[0]?.hero_image && (
                <img src={properties[0].hero_image} alt="Vyagram interior" className="h-full w-full rounded-2xl object-cover shadow-card" />
              )}
              <div className="absolute bottom-6 left-6 right-6 rounded-xl bg-parchment/95 p-5 backdrop-blur shadow-card">
                <p className="font-accent italic text-xl text-forest">Curated · hosted · remembered.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Love — horizontal continuous marquee */}
      {reviews.length > 0 && (
        <section className="vyg-section bg-forest text-parchment">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="vyg-eyebrow !text-gold">Guest Love</p>
                <h2 className="mt-3 font-display text-4xl lg:text-5xl text-parchment">Words from our guests</h2>
              </div>
              <Link to="/guest-love" className="vyg-link-gold !text-gold">All reviews <ArrowRight size={14}/></Link>
            </div>
          </div>
          <div className="mt-10">
            <GuestLoveMarquee reviews={reviews} properties={properties} />
          </div>
        </section>
      )}

      {/* Locations */}
      <section className="vyg-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <p className="vyg-eyebrow">Location-led discovery</p>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl">Find us in Delhi &amp; Gurugram</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {locations.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} data-testid={`location-card-${l.slug}`} className="group overflow-hidden rounded-2xl bg-white shadow-card">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={l.image} alt={l.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl">{l.name}</h3>
                  <p className="font-accent text-sm text-forest/70">{l.tagline}</p>
                  <p className="editorial-body mt-3 text-sm">{l.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="vyg-section bg-white/60">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <p className="vyg-eyebrow text-center">Before you ask</p>
          <h2 className="mt-3 text-center font-display text-4xl lg:text-5xl">Frequently asked</h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q || `faq-${i}`} value={`f-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="font-editorial text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="editorial-body">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <Link to="/faq" className="vyg-cta-ghost" data-testid="home-faq-cta">Read all FAQs</Link>
          </div>
        </div>
      </section>

      {/* Why Vyagram — calm editorial comparison vs typical stay platforms */}
      <WhyVyagramTable />

      {/* Final CTA — low-friction WhatsApp inquiry widget */}
      <FinalCTAWidget />
    </div>
  );
}
